import { AlpineComponent } from 'alpinejs';
import { initYouTubeTracking } from './youtube-tracker';

export type AnalyticEvent =
  | AnalyticEventInterface
  | AnalyticFormMultiStepInterface
  | AnalyticFilterInputInterface;

export interface AnalyticEventInterface {
  event: string;
  label: string;
  title?: string;
  current_page: string;
  user_language: string;
}

export interface AnalyticFormMultiStepInterface
  extends Omit<AnalyticEventInterface, 'label' | 'title'> {
  step: number;
}

export interface AnalyticFilterInputInterface extends AnalyticEventInterface {
  min?: string;
  max?: string;
}

export interface AnalyticInterface {
  datalayer: AnalyticEvent[] | null;
  enableGtm: boolean;
  start(): void;
  addEventListener(
    selector: string,
    type: 'click' | 'submit',
    listener: (event: Event) => void,
  ): void;
  push(data: AnalyticEvent): void;
  trigger(event: Event): void;
  handleDefaultListener(event: Event): void;
  handleFormListener(event: Event): void;
  handleFormStepChanged(event: Event): void;
  handleFilterInputBlur(event: Event, type: 'min' | 'max', value: string): void;
}

const Analytic: AlpineComponent<AnalyticInterface> = {
  datalayer: window.dataLayer || [],
  enableGtm: window.enable_gtm || false,

  addEventListener(
    selector: string,
    type: 'click' | 'submit',
    listener: (event: Event) => void,
  ): void {
    const elements = document.querySelectorAll(selector);
    if (elements) {
      elements.forEach((element: Element) => {
        element.addEventListener(type, listener.bind(this));
      });
    }
  },

  start(): void {
    this.addEventListener(
      'a[data-gtm]:not([data-gtm-manual])',
      'click',
      this.handleDefaultListener,
    );

    this.addEventListener(
      'button[data-gtm]:not([type="submit"]):not([data-gtm-manual])',
      'click',
      this.handleDefaultListener,
    );

    initYouTubeTracking();
  },

  handleDefaultListener(event: Event): void {
    const data = (<HTMLElement>event.currentTarget).dataset?.gtm;

    if (!data) return;

    const parsedData: AnalyticEvent = JSON.parse(data);

    this.push(parsedData);
  },

  handleFormListener(event: Event): void {
    const button = <HTMLButtonElement>event.target;

    const data = button?.dataset?.gtm;

    const form = button.closest<HTMLFormElement>('form.webform');

    if (!data || !form || !form.checkValidity()) return;

    const parsedData: AnalyticEvent = JSON.parse(data);

    this.push(parsedData);

    const [first] = document.getElementsByClassName('mhccna-webform-container');
    first?.scrollIntoView({ behavior: 'smooth' });
  },

  handleFormStepChanged(event: Event): void {
    const button = <HTMLButtonElement>event.target;
    const gtm = button?.dataset?.gtm ?? '';
    const parsedGtm = JSON.parse(gtm);
    const action = button?.dataset.action ?? '';

    const form: HTMLFormElement | null = (<HTMLButtonElement>(
      event.target
    )).closest('form.webform');

    if (['prev', 'next'].includes(action)) {
      // Only check the form validity when the user click on "next"
      if (action == 'next' && (!form || !form?.checkValidity())) return;

      const step = (<HTMLDivElement>(
        form?.querySelector('div[data-webform-key*="step"]')
      ))?.dataset?.webformKey;

      const stepNumber = Number(step?.split('_')[1]) ?? 0;

      if (gtm && step) {
        delete parsedGtm.label;

        const event: AnalyticFormMultiStepInterface = {
          ...parsedGtm,
          ...{ step: action == 'next' ? stepNumber + 1 : stepNumber - 1 },
        };

        this.push(event);
      }
    }
  },

  handleFilterInputBlur(
    event: Event,
    type: 'min' | 'max',
    value: string,
  ): void {
    const data = (<HTMLElement>event.target).dataset?.gtm;

    if (!data) return;

    const parsedData: AnalyticFilterInputInterface = {
      ...JSON.parse(data),
      ...{ [type]: value },
    };

    this.push(parsedData);
  },

  /**
   * Trigger manually the analytic push event.
   * (mainly used on dynamic scenarios where we use $store.analytic.trigger($event))
   *
   * @param event - The event object
   */
  trigger(event: Event): void {
    this.handleDefaultListener(event);
  },

  push(data: AnalyticEvent): void {
    this.enableGtm
      ? this.datalayer && this.datalayer.push(data)
      : console.log('>>> Analytic > event', data);
  },
};

export default Analytic;
